import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, Phone, Plus, Trash2 } from "lucide-react";
import { api, fmtDate, fmtDateTime, gbp, JOB_STATUS, telHref } from "@/admin/api";
import { Badge, Btn, Card, Empty, ErrorMsg, fileToDataUrl, inputCls, Loading, useAsync } from "@/admin/ui";
import { JobForm } from "@/admin/pages/Jobs";
import { BookingForm } from "@/admin/pages/Calendar";

const PhotoGroup = ({ job, kind, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const photos = job.photos.filter((p) => p.kind === kind);
  const upload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    setError(null);
    try {
      for (const file of files) {
        await api(`/admin/jobs/${job.id}/photos`, { method: "POST", body: { kind, data_url: await fileToDataUrl(file) } });
      }
      onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    await api(`/admin/jobs/${job.id}/photos/${id}`, { method: "DELETE" });
    onChange();
  };
  return (
    <div data-testid={`photos-${kind}`}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-bold text-navy">{kind === "before" ? "Before" : "After"} photos</h3>
        <Btn variant="outline" onClick={() => input.current.click()} disabled={busy} data-testid={`upload-${kind}`} className="min-h-11 px-3 text-sm">
          <Camera size={18} /> {busy ? "Uploading…" : "Add"}
        </Btn>
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={upload} data-testid={`file-${kind}`} />
      </div>
      <ErrorMsg error={error} />
      {photos.length === 0 ? <p className="text-sm text-body">None yet.</p> : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {photos.map((p) => (
            <li key={p.id} className="relative overflow-hidden rounded-xl bg-alt">
              <img src={p.data_url} alt={`${kind} photo for ${job.name}`} className="aspect-[4/3] w-full object-cover" />
              <button type="button" aria-label="Delete photo" data-testid={`delete-photo-${p.id}`} onClick={() => remove(p.id)} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-red-700"><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, error, loading, reload } = useAsync(() => api(`/admin/jobs/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const [booking, setBooking] = useState(false);
  const [note, setNote] = useState("");
  const [noteErr, setNoteErr] = useState(null);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg error={error} />;

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api(`/admin/jobs/${id}/updates`, { method: "POST", body: { text: note } });
      setNote("");
      reload();
    } catch (err) {
      setNoteErr(err.message);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${job.name} and everything attached to this job?`)) return;
    await api(`/admin/jobs/${id}`, { method: "DELETE" });
    navigate("/admin/jobs");
  };

  return (
    <div data-testid="job-detail-page" className="space-y-5">
      <Link to="/admin/jobs" className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-brand"><ArrowLeft size={16} /> All clients &amp; jobs</Link>
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 data-testid="job-title" className="text-2xl font-bold text-navy">{job.name}</h1>
            <p className="text-sm text-body">{job.work_type || "Type of work not set"}</p>
          </div>
          <Badge map={JOB_STATUS} value={job.status} testId="job-status-badge" />
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div><dt className="text-body">Phone</dt><dd className="font-semibold text-navy">{job.phone || "—"}</dd></div>
          <div><dt className="text-body">Value</dt><dd data-testid="job-value-display" className="font-semibold text-navy">{job.value ? gbp(job.value) : "—"}</dd></div>
          <div className="col-span-2"><dt className="text-body">Address</dt><dd className="font-semibold text-navy">{job.address || "—"}</dd></div>
          <div><dt className="text-body">Start</dt><dd className="font-semibold text-navy">{job.start_date ? fmtDate(job.start_date) : "—"}</dd></div>
          <div><dt className="text-body">Finish</dt><dd className="font-semibold text-navy">{job.end_date ? fmtDate(job.end_date) : "—"}</dd></div>
        </dl>
        {job.notes && <p className="mt-4 whitespace-pre-line rounded-xl bg-alt px-4 py-3 text-sm text-navy">{job.notes}</p>}
        <div className="mt-5 grid grid-cols-2 gap-3">
          {job.phone && <Btn as="a" variant="outline" href={telHref(job.phone)} data-testid="job-call"><Phone size={18} /> Call</Btn>}
          <Btn variant="outline" onClick={() => setEditing(true)} data-testid="job-edit">Edit details</Btn>
          <Btn variant="outline" onClick={() => setBooking(true)} data-testid="job-add-booking"><Plus size={18} /> Book visit / date</Btn>
          <Btn variant="danger" onClick={remove} data-testid="job-delete"><Trash2 size={18} /> Delete</Btn>
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-lg font-bold text-navy">Bookings</h2>
        {job.bookings.length === 0 ? <p className="text-sm text-body">No site visits or job dates booked.</p> : (
          <ul className="space-y-2 text-sm">
            {job.bookings.map((b) => <li key={b.id} data-testid={`job-booking-${b.id}`} className="rounded-xl bg-alt px-4 py-3 text-navy"><span className="font-semibold">{fmtDate(b.date)}{b.time ? ` · ${b.time}` : ""}</span> · {b.kind === "site_visit" ? "Site visit" : "Job date"}{b.notes ? ` · ${b.notes}` : ""}</li>)}
          </ul>
        )}
      </Card>

      <Card className="space-y-5">
        <PhotoGroup job={job} kind="before" onChange={reload} />
        <PhotoGroup job={job} kind="after" onChange={reload} />
      </Card>

      <Card>
        <h2 className="mb-3 text-lg font-bold text-navy">Job log</h2>
        <form onSubmit={addNote} className="flex gap-2">
          <input data-testid="log-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an update…" className={inputCls} />
          <Btn type="submit" data-testid="log-save" className="shrink-0 px-4">Add</Btn>
        </form>
        <ErrorMsg error={noteErr} />
        {job.updates.length === 0 ? <Empty testId="log-empty">No updates yet.</Empty> : (
          <ul className="mt-4 space-y-3">
            {job.updates.map((u) => (
              <li key={u.id} data-testid={`log-${u.id}`} className="border-l-2 border-brand pl-3">
                <p className="text-sm text-navy">{u.text}</p>
                <p className="text-xs text-body">{fmtDateTime(u.created_at)}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editing && <JobForm job={job} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); }} />}
      {booking && <BookingForm date={job.start_date || new Date().toISOString().slice(0, 10)} jobId={job.id} job={job} onClose={() => setBooking(false)} onSaved={() => { setBooking(false); reload(); }} />}
    </div>
  );
}
